import React, { useRef, useState } from "react";
import { Upload, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/apiClient";

const ALLOWED_EXT = [".pdf", ".jpg", ".jpeg", ".png", ".docx"];

const formatSize = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const DropUpload = ({ documents = [], onChange, testid = "drop-upload" }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const next = [...documents];
    for (const file of files) {
      const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) {
        toast.error(`${file.name}: unsupported file type`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await apiClient.post("/uploads/document", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push({
          file_id: data.file_id,
          filename: data.filename,
          size: data.size,
          uploaded_at: new Date().toISOString(),
        });
      } catch (e) {
        toast.error(`${file.name}: ${formatApiError(e)}`);
      }
    }
    onChange && onChange(next);
    setUploading(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    upload(Array.from(e.dataTransfer.files || []));
  };

  const remove = (idx) => {
    const next = [...documents];
    next.splice(idx, 1);
    onChange && onChange(next);
  };

  return (
    <div data-testid={testid}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`w-full min-h-[120px] rounded-[12px] border-2 border-dashed flex flex-col items-center justify-center gap-2 cursor-pointer transition-all duration-150 ${
          dragOver
            ? "border-accent-pink bg-accent-pink/5"
            : "border-accent-pink/60 bg-bg-primary hover:bg-accent-pink/5"
        }`}
        data-testid={`${testid}-dropzone`}
      >
        <Upload className="text-accent-pink" size={24} />
        <div className="text-[14px] text-text-secondary text-center px-4">
          {uploading
            ? "Uploading…"
            : "Drag & drop judgment documents here, or click to browse"}
        </div>
        <div className="text-[11px] text-text-muted">PDF, JPG, PNG, DOCX · up to 15 MB each</div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ALLOWED_EXT.join(",")}
          className="hidden"
          onChange={(e) => upload(Array.from(e.target.files || []))}
          data-testid={`${testid}-input`}
        />
      </div>

      {documents.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid={`${testid}-list`}>
          {documents.map((d, i) => (
            <li
              key={d.file_id || i}
              className="flex items-center justify-between bg-bg-tertiary border border-border-default rounded-[8px] px-3 py-2"
            >
              <div className="flex items-center gap-3 min-w-0">
                <FileText size={16} className="text-accent-pink shrink-0" />
                <div className="min-w-0">
                  <div className="text-[13px] text-white truncate">{d.filename}</div>
                  <div className="text-[11px] text-text-muted">{formatSize(d.size)}</div>
                </div>
              </div>
              <button
                type="button"
                aria-label={`Remove ${d.filename}`}
                onClick={() => remove(i)}
                className="p-1 rounded text-text-secondary hover:text-danger transition-colors"
                data-testid={`${testid}-remove-${i}`}
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DropUpload;
